import React, { useState, useRef, useEffect } from "react";
import useClx from "../../../hooks/use-clx";
import clxs from "./scrollable-tabs.module.css";
import fwclxs from "../../../styles/full-width.module.css";
import useTopProximitySpy from "./use-top-proximity-spy";

export interface TabData {
  label: string;
  elementRef: React.RefObject<HTMLElement>;
}

interface ScrollableTabsProps {
  tabsData: TabData[];
  className?: string;
  parentRef?: React.RefObject<HTMLElement>;
  headerOffset?: number;
}

function ScrollableTabs(props: ScrollableTabsProps) {
  const { tabsData, className, parentRef,headerOffset } = props;

  const [selectedIndex, setSelectedIndex] = useState(0);
  const suppressObserverRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();


  useTopProximitySpy(tabsData, setSelectedIndex, headerOffset, suppressObserverRef, parentRef);

  const ccx = useClx(
    fwclxs.container,
    fwclxs.containerPadding,
    clxs.scrollableTabs,
    className,
  );

  const tabContainerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleTabClick = (ref: React.RefObject<HTMLElement>, index: number) => {
    if (!ref.current) return;
    // Immediately update the selected index for visual feedback
    setSelectedIndex(index);

    // Disable auto-selection during scroll
    suppressObserverRef.current = true;

    const parentRoot = parentRef?.current;
    const headerOffsetSpace = headerOffset ? headerOffset - 20 : 180; // Use smaller offset inside scroll container
  
    // Calculate the target element’s top relative to its scroll container or window
    const elementPosition = ref.current.getBoundingClientRect().top;
    const parentElementPosition = parentRoot?.getBoundingClientRect().top ?? 0;
  
    // Determine scroll position based on context
    const offsetPosition = parentRoot
      ? parentRoot.scrollTop + (elementPosition - parentElementPosition - headerOffsetSpace)
      : window.pageYOffset + elementPosition - headerOffsetSpace;
  
    // Scroll smoothly to the target position
    (parentRoot ?? window).scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });

    // Reset suppression after scroll completes
    clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      suppressObserverRef.current = false;
    }, 1000); // Increased timeout to ensure scroll completes
  };

  // ✅ Auto-scroll to selected button in horizontal scroll area
  useEffect(() => {
    const container = tabContainerRef.current;
    const activeBtn = buttonRefs.current[selectedIndex];

    if (container && activeBtn) {
      const scrollLeft = activeBtn.offsetLeft - container.offsetWidth / 2 + activeBtn.offsetWidth / 2;

      container.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    }
  }, [selectedIndex]);

  return (
    <div 
      className={ccx}
      ref={tabContainerRef}
    >
      {tabsData.map((tab, index) => (
        <button
          key={index}
          ref={el => buttonRefs.current[index] = el}
          onClick={() => handleTabClick(tab.elementRef, index)}
          className={clxs.tabButton}
          data-selected={selectedIndex === index}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export default ScrollableTabs;
